/**
 * "Copiar con citas" — builds the plain-text clipboard payload for an assistant
 * answer: [cite:uuid] tokens become footnote numbers matching the inline
 * superscript numbering, markdown syntax is stripped, and a "Fuentes:" block
 * listing every citation is appended.
 *
 * Caller is expected to pass content with any trailing "Preguntas relacionadas"
 * block already removed (ChatBubble does this via extractSuggestions before
 * calling this util).
 */
import { Citation } from '../services/chatbot.service';
import { buildFootnoteEntry } from './citationFormat';

export interface AnswerExportData {
  content: string;
  citations?: Citation[];
}

const CITE_TOKEN = /\[cite:([a-f0-9-]+)\]/gi;

/** Strip markdown syntax while preserving clean line breaks — bold markers,
 *  heading hashes, blockquote markers are removed; bullets are normalized. */
function stripMarkdownSyntax(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*]\s+/gm, '- ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Build the plain-text export of an assistant answer: footnote-numbered
 * citations inline, followed by a "Fuentes:" section. Returns just the
 * stripped answer text when there are no citations.
 */
export function buildAnswerPlainText({ content, citations = [] }: AnswerExportData): string {
  const indexByUuid = new Map<string, number>();
  citations.forEach((c, i) => indexByUuid.set(c.document_id, i));

  const withFootnoteNumbers = content.replace(CITE_TOKEN, (_match, uuid) => {
    const idx = indexByUuid.get(uuid);
    return idx === undefined ? '' : `[${idx + 1}]`;
  });

  const plainText = stripMarkdownSyntax(withFootnoteNumbers);

  if (citations.length === 0) return plainText;

  const sourceLines = citations.map((c, i) => buildFootnoteEntry(c, i));
  return `${plainText}\n\nFuentes:\n${sourceLines.join('\n')}`;
}
